import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check, Tags } from "lucide-react";
import { fetchWithAuth, Tag } from "@/lib/api";

interface TagPickerProps {
  appliedTags: Tag[];
  onAdd: (tagId: string) => void;
  onRemove: (tagId: string) => void;
  align?: "start" | "end";
}

export function TagChip({ tag, onRemove }: { tag: Tag; onRemove?: () => void }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white leading-none"
      style={{ backgroundColor: tag.color ?? "#6b7280" }}
    >
      {tag.name}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-0.5 opacity-70 hover:opacity-100 leading-none"
          aria-label={`Remove tag ${tag.name}`}
        >
          ×
        </button>
      )}
    </span>
  );
}

export function TagPicker({ appliedTags, onAdd, onRemove, align = "start" }: TagPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: allTags = [] } = useQuery<Tag[]>({
    queryKey: ["tags"],
    queryFn: () => fetchWithAuth<Tag[]>("/api/tags"),
    enabled: open,
    staleTime: 30_000,
  });

  const appliedIds = new Set(appliedTags.map((t) => t.id));

  const filtered = allTags.filter((t) =>
    (t.name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = (tag: Tag) => {
    if (appliedIds.has(tag.id)) {
      onRemove(tag.id);
    } else {
      onAdd(tag.id);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors shrink-0"
          title="Add / remove tags"
        >
          <Tags className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-52 p-0"
        align={align}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2 border-b">
          <Input
            placeholder="Search tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
            autoFocus
          />
        </div>
        <div className="max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              {allTags.length === 0 ? "No tags yet — create some in Settings → Tags" : "No matches"}
            </p>
          ) : (
            filtered.map((tag) => {
              const applied = appliedIds.has(tag.id);
              return (
                <button
                  type="button"
                  key={tag.id}
                  onClick={(e) => { e.stopPropagation(); handleToggle(tag); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted transition-colors text-left"
                >
                  <span
                    className="inline-block w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: tag.color ?? "#6b7280" }}
                  />
                  <span className="flex-1 truncate">{tag.name}</span>
                  {applied && <Check className="w-3 h-3 text-primary shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
